import React, { useState } from "react";
import { ArrowRight, Home, Store, Camera } from "lucide-react";
import { SectionCard } from "../../../../Component/CustComponents/SectionCard";
import ImageUpload from "../../Retailer/CreateCustomer/ImageUpload";

export default function RaiseClaimForm({
  warranties = [],
  customer,
  onSubmitClaim,
  submitting,
  warrantiesLoading = false, // 👈 NEW
}) {
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    warrantyId: "",
    issueSummary: "",
    extraDetails: "",
    addressType: "",
    images: Array(6).fill(null),
  });

  const canGoNextStep1 = form.warrantyId && form.issueSummary;
  const canGoNextStep2 = form.addressType;

  const customerAddress = customer && (
    <>
      <div>{customer.name}</div>
      <div>{customer.location}</div>
      <div>
        {customer.city}, {customer.state} - {customer.pincode}
      </div>
    </>
  );

  const retailerAddress = customer?.retailer && (
    <>
      <div>{customer.retailer.business_name}</div>
      <div>{customer.retailer.address_line1}</div>
      <div>
        {customer.retailer.city}, {customer.retailer.state} -{" "}
        {customer.retailer.pincode}
      </div>
    </>
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    const uploadedImages = form.images.filter(Boolean);
    if (uploadedImages.length === 0) {
      alert("Please upload at least one image.");
      return;
    }

    onSubmitClaim({ ...form, images: uploadedImages });

    setForm({
      warrantyId: "",
      issueSummary: "",
      extraDetails: "",
      addressType: "",
      images: Array(6).fill(null),
    });
    setStep(1);
  };

  return (
    <SectionCard
      title="Raise a New Claim"
      right={
        <span className="hidden md:inline text-[12px] text-slate-400">
          Step {step} of 3
        </span>
      }
    >
      {/* ================= STEP 1 ================= */}
      {step === 1 && (
        <form className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {/* WARRANTY SELECT */}
          <div className="md:col-span-2">
            <label className="text-sm text-slate-600">
              Select Warranty *
            </label>

            <div className="relative">
              <select
                disabled={warrantiesLoading}
                className={`border rounded-lg px-2 py-1.5 bg-white w-full ${
                  warrantiesLoading ? "opacity-60 cursor-not-allowed" : ""
                }`}
                value={form.warrantyId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, warrantyId: e.target.value }))
                }
              >
                {warrantiesLoading ? (
                  <option>Loading warranties...</option>
                ) : (
                  <>
                    <option value="">Choose your product</option>
                    {warranties.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.product_name} · Exp {w.expiry_date} · Claims left{" "}
                        {w.available_claim}
                      </option>
                    ))}
                  </>
                )}
              </select>

              {warrantiesLoading && (
                <span className="absolute right-3 top-2.5 h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              )}
            </div>
          </div>

          {/* ISSUE */}
          <div className="md:col-span-2">
            <label className="text-sm text-slate-600">
              Issue Summary *
            </label>
            <input
              className="border rounded-lg px-2 py-1.5 w-full bg-white"
              value={form.issueSummary}
              onChange={(e) =>
                setForm((f) => ({ ...f, issueSummary: e.target.value }))
              }
            />
          </div>

          {/* EXTRA */}
          <div className="md:col-span-2">
            <label className="text-sm text-slate-600">Extra Details</label>
            <textarea
              className="border rounded-lg px-2 py-1.5 w-full bg-white min-h-[70px]"
              value={form.extraDetails}
              onChange={(e) =>
                setForm((f) => ({ ...f, extraDetails: e.target.value }))
              }
            />
          </div>

          <div className="md:col-span-2 flex justify-end">
            <button
              disabled={!canGoNextStep1}
              onClick={() => setStep(2)}
              className="px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-50"
            >
              Next <ArrowRight className="w-3 h-3 inline" />
            </button>
          </div>
        </form>
      )}

      {/* ================= STEP 2 ================= */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div
              onClick={() =>
                setForm((f) => ({ ...f, addressType: "retailer" }))
              }
              className={`cursor-pointer rounded-xl border p-4 ${
                form.addressType === "retailer"
                  ? "border-blue-600 bg-blue-50"
                  : ""
              }`}
            >
              <Store className="text-blue-600 mb-1" />
              {retailerAddress}
            </div>

            <div
              onClick={() =>
                setForm((f) => ({ ...f, addressType: "self" }))
              }
              className={`cursor-pointer rounded-xl border p-4 ${
                form.addressType === "self"
                  ? "border-blue-600 bg-blue-50"
                  : ""
              }`}
            >
              <Home className="text-blue-600 mb-1" />
              {customerAddress}
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="px-3 py-1.5 rounded-lg border"
            >
              Back
            </button>
            <button
              disabled={!canGoNextStep2}
              onClick={() => setStep(3)}
              className="px-3 py-1.5 rounded-lg bg-blue-600 text-white"
            >
              Next <ArrowRight className="w-3 h-3 inline" />
            </button>
          </div>
        </div>
      )}

      {/* ================= STEP 3 ================= */}
      {step === 3 && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {form.images.map((img, i) => (
              <ImageUpload
                key={i}
                label={`Image ${i + 1}`}
                value={img}
                onChange={(file) => {
                  const updated = [...form.images];
                  updated[i] = file;
                  setForm((f) => ({ ...f, images: updated }));
                }}
              />
            ))}
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="px-3 py-1.5 rounded-lg border"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-3 py-1.5 rounded-lg bg-blue-600 text-white"
            >
              <Camera className="w-3 h-3 inline" /> Submit Claim
            </button>
          </div>
        </form>
      )}
    </SectionCard>
  );
}



// import React, { useState } from "react";
// import { PlusCircle, ArrowRight, Home, Store, Camera } from "lucide-react";
// import { SectionCard } from "../../../../Component/CustComponents/SectionCard";

// export default function RaiseClaimForm({
//   warranties = [],
//   onSubmitClaim,
//   submitting,
// }) {
//   const [step, setStep] = useState(1);

//   const [form, setForm] = useState({
//     warrantyId: "",
//     issueSummary: "",
//     extraDetails: "",
//     addressType: "", // retailer | self
//     photo: null,
//   });

//   /* ---------------- STEP VALIDATION ---------------- */

//   const canGoNextStep1 =
//     form.warrantyId && form.issueSummary;

//   const canGoNextStep2 =
//     form.addressType;

//   /* ---------------- SUBMIT ---------------- */

//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (!form.photo) {
//       alert("Please upload a photo.");
//       return;
//     }

//     onSubmitClaim(form);

//     // reset
//     setForm({
//       warrantyId: "",
//       issueSummary: "",
//       extraDetails: "",
//       addressType: "",
//       photo: null,
//     });
//     setStep(1);
//   };

//   return (
//     <SectionCard
//       title="Raise a New Claim"
//       right={
//         <span className="hidden md:inline text-[12px] text-slate-400">
//           Step {step} of 3
//         </span>
//       }
//     >
//       {/* ================= STEP 1 ================= */}
//       {step === 1 && (
//         <form className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
//           {/* Warranty */}
//           <div className="md:col-span-2 flex flex-col gap-1">
//             <label className="text-sm text-slate-600">
//               Select Warranty *
//             </label>
//             <select
//               className="border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
//               value={form.warrantyId}
//               onChange={(e) =>
//                 setForm((f) => ({ ...f, warrantyId: e.target.value }))
//               }
//             >
//               <option value="">Choose your product warranty</option>
//               {warranties.map((w) => (
//                 <option key={w.id} value={w.id}>
//                   {w.productName} · {w.serialOrImei}
//                 </option>
//               ))}
//             </select>
//           </div>

//           {/* Issue */}
//           <div className="md:col-span-2 flex flex-col gap-1">
//             <label className="text-sm text-slate-600">
//               Issue Summary *
//             </label>
//             <input
//               type="text"
//               value={form.issueSummary}
//               onChange={(e) =>
//                 setForm((f) => ({ ...f, issueSummary: e.target.value }))
//               }
//               className="border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
//               placeholder="e.g. Device not powering on"
//             />
//           </div>

//           {/* Extra */}
//           <div className="md:col-span-2 flex flex-col gap-1">
//             <label className="text-sm text-slate-600">
//               Extra Details
//             </label>
//             <textarea
//               className="border border-slate-200 rounded-lg px-2 py-1.5 bg-white min-h-[70px]"
//               value={form.extraDetails}
//               onChange={(e) =>
//                 setForm((f) => ({ ...f, extraDetails: e.target.value }))
//               }
//             />
//           </div>

//           {/* NEXT */}
//           <div className="md:col-span-2 flex justify-end">
//             <button
//               disabled={!canGoNextStep1}
//               onClick={() => setStep(2)}
//               className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-50"
//             >
//               Next <ArrowRight className="w-3 h-3" />
//             </button>
//           </div>
//         </form>
//       )}

//       {/* ================= STEP 2 ================= */}
//       {step === 2 && (
//         <div className="space-y-4">
//           <p className="text-sm text-slate-600">
//             Select Address Type
//           </p>

//           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//             {/* Retailer */}
//             <div
//               onClick={() =>
//                 setForm((f) => ({ ...f, addressType: "retailer" }))
//               }
//               className={`cursor-pointer rounded-xl border p-4 text-center
//                 ${
//                   form.addressType === "retailer"
//                     ? "border-blue-600 bg-blue-50"
//                     : "border-slate-200"
//                 }`}
//             >
//               <Store className="mx-auto mb-2 text-blue-600" />
//               <div className="font-medium">Retailer Address</div>
//             </div>

//             {/* Self */}
//             <div
//               onClick={() =>
//                 setForm((f) => ({ ...f, addressType: "self" }))
//               }
//               className={`cursor-pointer rounded-xl border p-4 text-center
//                 ${
//                   form.addressType === "self"
//                     ? "border-blue-600 bg-blue-50"
//                     : "border-slate-200"
//                 }`}
//             >
//               <Home className="mx-auto mb-2 text-blue-600" />
//               <div className="font-medium">Self Address</div>
//             </div>
//           </div>

//           <div className="flex justify-between">
//             <button
//               onClick={() => setStep(1)}
//               className="px-3 py-1.5 rounded-lg border text-sm"
//             >
//               Back
//             </button>

//             <button
//               disabled={!canGoNextStep2}
//               onClick={() => setStep(3)}
//               className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-50"
//             >
//               Next <ArrowRight className="w-3 h-3" />
//             </button>
//           </div>
//         </div>
//       )}

//       {/* ================= STEP 3 ================= */}
//       {step === 3 && (
//         <form onSubmit={handleSubmit} className="space-y-4">
//           <label className="text-sm text-slate-600">
//             Upload Device Photo *
//           </label>

//           <input
//             type="file"
//             accept="image/*"
//             onChange={(e) =>
//               setForm((f) => ({ ...f, photo: e.target.files[0] }))
//             }
//             className="text-xs"
//           />

//           <div className="flex justify-between">
//             <button
//               type="button"
//               onClick={() => setStep(2)}
//               className="px-3 py-1.5 rounded-lg border text-sm"
//             >
//               Back
//             </button>

//             <button
//               type="submit"
//               disabled={submitting}
//               className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white"
//             >
//               <Camera className="w-3 h-3" />
//               {submitting ? "Submitting..." : "Submit Claim"}
//             </button>
//           </div>
//         </form>
//       )}
//     </SectionCard>
//   );
// }



// import React, { useState } from "react";
// import { PlusCircle } from "lucide-react";
// import { SectionCard } from "../../../../Component/CustComponents/SectionCard";


// export default function RaiseClaimForm({
//   warranties = [],
//   onSubmitClaim,
//   submitting,
// }) {
//   const [form, setForm] = useState({
//     warrantyId: "",
//     issueSummary: "",
//     extraDetails: "",
//   });

//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (!form.warrantyId || !form.issueSummary) {
//       alert("Please select a warranty and describe the issue.");
//       return;
//     }

//     onSubmitClaim(form);
//     setForm({ warrantyId: "", issueSummary: "", extraDetails: "" });
//   };

//   return (
//     <SectionCard
//       title="Raise a New Claim"
//       right={
//         <span className="hidden md:inline text-[12px] text-slate-400">
//           Select the device and describe the issue clearly
//         </span>
//       }
//     >
//       <form
//         onSubmit={handleSubmit}
//         className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs"
//       >
//         {/* Warranty */}
//         <div className="md:col-span-2 flex flex-col gap-1">
//           <label className="text-sm text-slate-600">
//             Select Warranty *
//           </label>
//           <select
//             className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white"
//             value={form.warrantyId}
//             onChange={(e) =>
//               setForm((f) => ({ ...f, warrantyId: e.target.value }))
//             }
//           >
//             <option value="">Choose your product warranty</option>
//             {warranties.map((w) => (
//               <option key={w.id} value={w.id}>
//                 {w.productName} · {w.serialOrImei} · Expires {w.expiryDate}
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* Issue */}
//         <div className="md:col-span-2 flex flex-col gap-1">
//           <label className="text-sm text-slate-600">
//             Issue Summary *
//           </label>
//           <input
//             type="text"
//             value={form.issueSummary}
//             onChange={(e) =>
//               setForm((f) => ({ ...f, issueSummary: e.target.value }))
//             }
//             className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white"
//             placeholder="e.g. Device not powering on"
//           />
//         </div>

//         {/* Extra Details */}
//         <div className="md:col-span-2 flex flex-col gap-1">
//           <label className="text-sm text-slate-600">
//             Extra Details
//           </label>
//           <textarea
//             className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white min-h-[70px]"
//             value={form.extraDetails}
//             onChange={(e) =>
//               setForm((f) => ({ ...f, extraDetails: e.target.value }))
//             }
//             placeholder="Mention extra details"
//           />
//         </div>

//         {/* Submit */}
//         <div className="md:col-span-2 flex justify-between items-center">
//           <span className="text-sm text-slate-500">
//             By submitting, you confirm the details are correct.
//           </span>

//           <button
//             disabled={submitting}
//             type="submit"
//             className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white"
//           >
//             <PlusCircle className="w-3 h-3" />
//             {submitting ? "Submitting..." : "Submit Claim"}
//           </button>
//         </div>
//       </form>
//     </SectionCard>
//   );
// }
